import { useQuery } from '@tanstack/react-query';
import { Building2, DoorOpen, Search, Tag, UserRound, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { api } from '../lib/api';
import { ASSET_STATUS_LABEL, initials, personName } from '../lib/format';
import { useBuildings, useDebouncedValue, useRooms } from '../lib/hooks';
import type { AssetListItem, AssetStatus, Page, PersonListItem, PersonSummary, Room } from '../lib/types';
import { PersonTypeBadge } from './badges';
import { cn, Input, Select, Spinner } from './ui';

// ---------------------------------------------------------------------------
// Combobox
// ---------------------------------------------------------------------------

export function Combobox<T>({
  items,
  loading,
  query,
  onQueryChange,
  onSelect,
  renderItem,
  getKey,
  placeholder,
  emptyText = 'No matches',
  autoFocus,
  inline,
  inputRef,
  showWhenEmpty,
}: {
  items: T[];
  loading?: boolean;
  query: string;
  onQueryChange(value: string): void;
  onSelect(item: T): void;
  renderItem(item: T): ReactNode;
  getKey(item: T): string;
  placeholder?: string;
  emptyText?: string;
  autoFocus?: boolean;
  /** Render results in the document flow (inside modals, where overflow would clip a popover). */
  inline?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  showWhenEmpty?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => setActive(0), [items]);
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const choose = (item: T) => {
    onSelect(item);
    setOpen(false);
  };

  const visible = open && (showWhenEmpty || query.trim().length > 0);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-slate-400" />
        <Input
          ref={inputRef}
          value={query}
          autoFocus={autoFocus}
          placeholder={placeholder}
          className="pr-8 pl-8"
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            onQueryChange(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActive((i) => Math.min(i + 1, items.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (event.key === 'Enter' && visible && items[active]) {
              event.preventDefault();
              choose(items[active]);
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        {loading && <Spinner className="absolute top-1/2 right-2.5 size-4 -translate-y-1/2" />}
      </div>
      {visible && (
        <ul
          className={cn(
            'max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1',
            inline ? 'mt-2' : 'absolute z-30 mt-1 shadow-lg',
          )}
        >
          {items.length === 0 ? (
            <li className="px-3 py-2 text-sm text-slate-500">{loading ? 'Searching…' : emptyText}</li>
          ) : (
            items.map((item, index) => (
              <li key={getKey(item)}>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(item)}
                  className={cn('flex w-full items-center gap-3 px-3 py-2 text-left text-sm', index === active && 'bg-brand-50')}
                >
                  {renderItem(item)}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <div className={cn('flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800', className)}>
      {initials(name)}
    </div>
  );
}

function SelectedCard({ icon, title, subtitle, extra, onClear }: { icon: ReactNode; title: ReactNode; subtitle?: ReactNode; extra?: ReactNode; onClear(): void }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-brand-200 bg-brand-50/50 px-3 py-2">
      {icon}
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
          {title}
          {extra}
        </p>
        {subtitle && <p className="truncate text-xs text-slate-500">{subtitle}</p>}
      </div>
      <button type="button" onClick={onClear} className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-slate-700" aria-label="Clear">
        <X className="size-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export function PersonPicker({
  value,
  onChange,
  inline,
  autoFocus,
  placeholder = 'Search by name, email, or student ID',
}: {
  value: PersonSummary | null;
  onChange(person: PersonSummary | null): void;
  inline?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const search = useDebouncedValue(query.trim(), 250);
  const results = useQuery({
    queryKey: ['people', 'picker', search],
    queryFn: () => api.get<Page<PersonListItem>>('/people', { search, pageSize: 8, status: 'ACTIVE' }),
    enabled: search.length > 0,
  });

  if (value) {
    return (
      <SelectedCard
        icon={<Avatar name={personName(value)} />}
        title={personName(value)}
        extra={<PersonTypeBadge type={value.type} />}
        subtitle={[value.email, value.gradYear && `Class of ${value.gradYear}`].filter(Boolean).join(' · ')}
        onClear={() => onChange(null)}
      />
    );
  }

  return (
    <Combobox
      inline={inline}
      autoFocus={autoFocus}
      query={query}
      onQueryChange={setQuery}
      loading={results.isFetching}
      items={search ? (results.data?.items ?? []) : []}
      getKey={(p) => p.id}
      placeholder={placeholder}
      emptyText="No active people match"
      onSelect={(person) => {
        onChange(person);
        setQuery('');
      }}
      renderItem={(person) => (
        <>
          <Avatar name={personName(person)} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-slate-900">{personName(person)}</p>
            <p className="truncate text-xs text-slate-500">
              {[person.email, person.gradYear && `Class of ${person.gradYear}`, person.hostId].filter(Boolean).join(' · ')}
            </p>
          </div>
          <PersonTypeBadge type={person.type} />
        </>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Rooms & buildings
// ---------------------------------------------------------------------------

export function RoomPicker({
  value,
  onChange,
  inline,
}: {
  value: Room | null;
  onChange(room: Room | null): void;
  inline?: boolean;
}) {
  const rooms = useRooms();
  const [query, setQuery] = useState('');
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rooms.data ?? [])
      .filter((room) => !q || `${room.name} ${room.roomNumber ?? ''} ${room.building?.name ?? ''}`.toLowerCase().includes(q))
      .slice(0, 30);
  }, [rooms.data, query]);

  if (value) {
    return (
      <SelectedCard
        icon={<DoorOpen className="size-5 text-brand-700" />}
        title={value.name}
        subtitle={value.building?.name}
        onClear={() => onChange(null)}
      />
    );
  }
  return (
    <Combobox
      inline={inline}
      showWhenEmpty
      query={query}
      onQueryChange={setQuery}
      loading={rooms.isLoading}
      items={matches}
      getKey={(room) => room.id}
      placeholder="Search rooms"
      emptyText="No rooms match"
      onSelect={(room) => {
        onChange(room);
        setQuery('');
      }}
      renderItem={(room) => (
        <>
          <DoorOpen className="size-4 shrink-0 text-slate-400" />
          <span className="flex-1 truncate font-medium text-slate-900">{room.name}</span>
          <span className="text-xs text-slate-500">{room.building?.name}</span>
        </>
      )}
    />
  );
}

export function BuildingSelect({
  value,
  onChange,
  placeholder = 'Select a building',
  className,
}: {
  value: string;
  onChange(id: string): void;
  placeholder?: string;
  className?: string;
}) {
  const buildings = useBuildings();
  return (
    <Select value={value} onChange={(event) => onChange(event.target.value)} className={className}>
      <option value="">{placeholder}</option>
      {(buildings.data ?? []).map((building) => (
        <option key={building.id} value={building.id}>
          {building.name}
        </option>
      ))}
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

export function AssetSearch({
  onSelect,
  status,
  placeholder = 'Search by asset tag, serial, or name',
  inline,
  excludeIds = [],
}: {
  onSelect(asset: AssetListItem): void;
  status?: AssetStatus[];
  placeholder?: string;
  inline?: boolean;
  excludeIds?: string[];
}) {
  const [query, setQuery] = useState('');
  const search = useDebouncedValue(query.trim(), 250);
  const results = useQuery({
    queryKey: ['assets', 'picker', search, status],
    queryFn: () => api.get<Page<AssetListItem>>('/assets', { search, pageSize: 10, status }),
    enabled: search.length > 0,
  });
  const items = (results.data?.items ?? []).filter((asset) => !excludeIds.includes(asset.id));

  return (
    <Combobox
      inline={inline}
      query={query}
      onQueryChange={setQuery}
      loading={results.isFetching}
      items={search ? items : []}
      getKey={(asset) => asset.id}
      placeholder={placeholder}
      emptyText={status?.length ? `No ${status.map((s) => ASSET_STATUS_LABEL[s].toLowerCase()).join(' or ')} assets match` : 'No assets match'}
      onSelect={(asset) => {
        onSelect(asset);
        setQuery('');
      }}
      renderItem={(asset) => (
        <>
          <span className="w-20 shrink-0 font-mono text-xs font-semibold text-brand-800">{asset.assetTag}</span>
          <span className="min-w-0 flex-1 truncate text-slate-900">{asset.name}</span>
          <span className="truncate text-xs text-slate-500">{asset.serialNumber}</span>
        </>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Assignee (person / room / building / other)
// ---------------------------------------------------------------------------

export type AssigneeValue =
  | { type: 'PERSON'; person: PersonSummary | null }
  | { type: 'ROOM'; room: Room | null }
  | { type: 'BUILDING'; buildingId: string }
  | { type: 'OTHER'; label: string };

export function assigneeToInput(value: AssigneeValue) {
  switch (value.type) {
    case 'PERSON':
      return value.person ? { type: 'PERSON' as const, personId: value.person.id } : null;
    case 'ROOM':
      return value.room ? { type: 'ROOM' as const, roomId: value.room.id } : null;
    case 'BUILDING':
      return value.buildingId ? { type: 'BUILDING' as const, buildingId: value.buildingId } : null;
    case 'OTHER':
      return value.label.trim() ? { type: 'OTHER' as const, label: value.label.trim() } : null;
  }
}

export const ASSIGNEE_OPTIONS = [
  { type: 'PERSON', label: 'Person', icon: UserRound },
  { type: 'ROOM', label: 'Room', icon: DoorOpen },
  { type: 'BUILDING', label: 'Building', icon: Building2 },
  { type: 'OTHER', label: 'Other', icon: Tag },
] as const;

/**
 * Checking out to a room asks whether the room also becomes where the devices are kept. The answer belongs to the room
 * it was given for, so choosing another room asks again.
 */
export function useHomeLocationAnswer(assignee: AssigneeValue) {
  const room = assignee.type === 'ROOM' ? assignee.room : null;
  const [answer, setAnswer] = useState<{ roomId: string; value: boolean } | null>(null);
  const value = room && answer?.roomId === room.id ? answer.value : null;
  return {
    room,
    value,
    set: (next: boolean) => {
      if (room) setAnswer({ roomId: room.id, value: next });
    },
    /** A check-out to a room waits for the answer. */
    ready: !room || value !== null,
    body: () => (room ? { setHomeLocation: value === true } : {}),
    reset: () => setAnswer(null),
  };
}

export function HomeLocationQuestion({ room, value, onChange }: { room: Room; value: boolean | null; onChange(value: boolean): void }) {
  const name = useId();
  const place = room.building ? `${room.name} (${room.building.name})` : room.name;
  const options: [boolean, string][] = [
    [true, 'Yes, make it the home location'],
    [false, 'No, keep its current home location'],
  ];
  return (
    <fieldset className="rounded-lg border border-brand-200 bg-brand-50/50 px-3 py-2.5">
      <legend className="float-left text-sm font-medium text-slate-900">Make {place} the home location too?</legend>
      <p className="clear-left pt-0.5 text-xs text-slate-500">The home location is where a device is kept. Devices on a cart stay with their cart.</p>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5">
        {options.map(([option, label]) => (
          <label key={label} className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input type="radio" name={name} checked={value === option} onChange={() => onChange(option)} className="accent-brand-700" />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function AssigneePicker({ value, onChange, inline }: { value: AssigneeValue; onChange(value: AssigneeValue): void; inline?: boolean }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-1 rounded-lg bg-slate-100 p-1">
        {ASSIGNEE_OPTIONS.map((option) => (
          <button
            key={option.type}
            type="button"
            onClick={() => {
              if (option.type === value.type) return;
              if (option.type === 'PERSON') onChange({ type: 'PERSON', person: null });
              if (option.type === 'ROOM') onChange({ type: 'ROOM', room: null });
              if (option.type === 'BUILDING') onChange({ type: 'BUILDING', buildingId: '' });
              if (option.type === 'OTHER') onChange({ type: 'OTHER', label: '' });
            }}
            className={cn(
              'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
              value.type === option.type ? 'bg-white text-brand-800 shadow-sm' : 'text-slate-600 hover:text-slate-900',
            )}
          >
            <option.icon className="size-3.5" />
            {option.label}
          </button>
        ))}
      </div>
      {value.type === 'PERSON' && (
        <PersonPicker inline={inline} autoFocus value={value.person} onChange={(person) => onChange({ type: 'PERSON', person })} />
      )}
      {value.type === 'ROOM' && <RoomPicker inline={inline} value={value.room} onChange={(room) => onChange({ type: 'ROOM', room })} />}
      {value.type === 'BUILDING' && (
        <BuildingSelect value={value.buildingId} onChange={(buildingId) => onChange({ type: 'BUILDING', buildingId })} />
      )}
      {value.type === 'OTHER' && (
        <Input
          autoFocus
          value={value.label}
          maxLength={200}
          placeholder="e.g. Spring musical, Loaner pool, Department of Athletics"
          onChange={(event) => onChange({ type: 'OTHER', label: event.target.value })}
        />
      )}
    </div>
  );
}
